import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Search, 
  Plus,
  Calendar,
  Upload,
  Users,
  BarChart,
  CheckSquare,
  Clock,
  Home,
  Menu,
  X
} from 'lucide-react';
import { useAdminAuth } from '../hooks/useAdminAuth';

/**
 * SalesLayout component - Responsive layout wrapper for sales interface
 * Provides mobile-responsive navigation with hamburger menu and desktop sidebar
 * Includes only navigation items relevant to sales team (no Create/Manage/Import)
 */
const SalesLayout = ({ children }) => {
  // Get current route location for active navigation state
  const location = useLocation();
  // Control mobile menu visibility state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Check if user has admin access
  const { isAdminAuthenticated } = useAdminAuth();
  
  // Base navigation items for sales users
  const baseNavItems = [
    {
      path: '/sales/dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Overview and key metrics'
    },
    {
      path: '/sales/scheduled-offerings/search',
      label: 'Search SOs',
      icon: Search,
      description: 'Find and view scheduled offerings'
    },
    {
      path: '/sales/facilitator-hours',
      label: 'Facilitator Hours',
      icon: Clock,
      description: 'View facilitator workload and hours by SO'
    },
    {
      path: '/sales/clients',
      label: 'Search Clients',
      icon: Users,
      description: 'Find and view client information'
    },
    {
      path: '/sales/surveys',
      label: 'Show Surveys',
      icon: BarChart,
      description: 'View survey responses and analytics',
      placeholder: true
    },
    {
      path: '/sales/attendance',
      label: 'Check Attendance',
      icon: CheckSquare,
      description: 'Use Search SOs to look up attendance by SO number',
      placeholder: true
    }
  ];

  // Additional admin-only navigation items
  const adminOnlyItems = [
    {
      path: '/admin/scheduled-offerings/create',
      label: 'Create SO',
      icon: Plus,
      description: 'Create new scheduled offering'
    },
    {
      path: '/admin/sessions/manage',
      label: 'Manage Sessions',
      icon: Calendar,
      description: 'Add and manage sessions'
    },
    {
      path: '/admin/import/attendees',
      label: 'Import Attendees',
      icon: Upload,
      description: 'Upload CSV data and templates'
    }
  ];

  // Combine nav items based on admin status
  const navItems = isAdminAuthenticated 
    ? [...baseNavItems, ...adminOnlyItems]
    : baseNavItems;

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
              <h1 className="text-xl font-semibold text-gray-900">Sales Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                size="sm" 
                asChild
                className="flex items-center space-x-2"
              >
                <Link to="/">
                  <Home className="h-4 w-4" />
                  <span className="hidden sm:inline">Back to App</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="fixed inset-y-0 left-0 w-80 max-w-full bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Navigation</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <nav className="space-y-2">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isPlaceholder = item.placeholder;
                  
                  if (isPlaceholder) {
                    return (
                      <div 
                        key={item.path}
                        className="flex items-start space-x-3 p-4 rounded-lg transition-all duration-200 group cursor-not-allowed opacity-60"
                      >
                        <Icon className="h-5 w-5 flex-shrink-0 mt-0.5 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-base text-gray-500">
                            {item.label}
                          </div>
                          <div className="text-sm mt-1 leading-tight text-gray-400">
                            Coming Soon
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <Link 
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      aria-current={isActive(item.path) ? 'page' : undefined}
                      className={`
                        flex items-start space-x-3 p-4 rounded-lg transition-all duration-200 group
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                        min-h-[44px] touch-manipulation
                        ${isActive(item.path) 
                          ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                    >
                      <Icon className={`
                        h-5 w-5 flex-shrink-0 mt-0.5
                        ${isActive(item.path) ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}
                      `} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-base">
                          {item.label}
                        </div>
                        <div className={`
                          text-sm mt-1 leading-tight
                          ${isActive(item.path) ? 'text-blue-600' : 'text-gray-500'}
                        `}>
                          {item.description}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Desktop Sidebar Navigation */}
          <div className="hidden lg:block lg:col-span-1">
            <Card>
              <CardContent className="p-6">
                <nav className="space-y-2">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isPlaceholder = item.placeholder;
                    
                    if (isPlaceholder) {
                      return (
                        <div 
                          key={item.path}
                          className="flex items-start space-x-3 p-3 rounded-lg transition-all duration-200 group cursor-not-allowed opacity-60"
                        >
                          <Icon className="h-5 w-5 flex-shrink-0 mt-0.5 text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-gray-500">
                              {item.label}
                            </div>
                            <div className="text-xs mt-1 leading-tight text-gray-400">
                              Coming Soon
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <Link 
                        key={item.path}
                        to={item.path}
                        aria-current={isActive(item.path) ? 'page' : undefined}
                        className={`
                          flex items-start space-x-3 p-3 rounded-lg transition-all duration-200 group
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                          min-h-[44px] touch-manipulation
                          ${isActive(item.path) 
                            ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        <Icon className={`
                          h-5 w-5 flex-shrink-0 mt-0.5
                          ${isActive(item.path) ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}
                        `} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">
                            {item.label}
                          </div>
                          <div className={`
                            text-xs mt-1 leading-tight
                            ${isActive(item.path) ? 'text-blue-600' : 'text-gray-500'}
                          `}>
                            {item.description}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="col-span-1 lg:col-span-3">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesLayout;